/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Badge } from 'src/core/components/Badge';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getTranslatedEntityType } from 'src/luis/modules/entities/utils/translatedEntityType';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import {
    SelectOrCreateEntityRole,
    SelectOrCreateEntityRoleProps
} from 'src/luis/verticals/labelingSidePane/components/SelectOrCreateEntityRole';

const messages = defineMessages({
    childEntities: {
        id: 'luis.intentLabelingSidePane.advancedOptions.compositeEntity.childEntities',
        defaultMessage: 'child entities'
    }
});

const scopedTheme = getScopedTheme('AdvancedOptionsPane');

const childrenTitleStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { textTransform: 'uppercase', fontSize: 10, color: scopedTheme.titleTextColor }
});

export type CompositeEntityAdvancedOptionsProps = SelectOrCreateEntityRoleProps & {
    entity: CompositeEntity;
};

/**
 * Renders advanced options for a role-based entity.
 * Regex and prebuilt entities for now.
 */
export const CompositeEntityAdvancedOptions = React.memo((props: CompositeEntityAdvancedOptionsProps) => {
    const { entity, ...roleProps } = props;
    const { children, roles } = entity;

    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {roles.length && <SelectOrCreateEntityRole entity={entity} {...roleProps} />}
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText fontWeight={600} styles={childrenTitleStyles}>
                    {localize(messages.childEntities)}
                </LuisText>
                {children.map(child => (
                    <Stack verticalAlign="center" horizontal wrap tokens={{ childrenGap: 8 }} key={child.id}>
                        <LuisText title={child.name}>{child.name}</LuisText>
                        <Badge text={getTranslatedEntityType(child.typeId, localize)} />
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
});
